import { onMounted, onUnmounted, ref, type Ref } from "vue";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";

/** Shared pointer-drag promotion threshold — matches `useOutlineDrag`. */
export const POINTER_ROW_DRAG_THRESHOLD_PX = 4;

/**
 * Pointer-based row reordering. HTML5 DnD is unreliable in the Tauri WebView;
 * this is the same model `useOutlineDrag` uses for chapters/scenes.
 *
 * Drop on a row = take that row's exact slot (`reorderExactSlotByKey`),
 * same as `reorderChaptersExactSlot` / `reorderScenesExactSlot`.
 */
export function usePointerRowDrag<T extends { key: string }>(options: {
  rowAtPoint: (clientX: number, clientY: number) => Maybe<string>;
  onDrop: (from: T, toKey: string) => void;
  shouldIgnorePointerDown?: (target: HTMLElement) => boolean;
}): {
  dragFrom: Ref<Maybe<T>>;
  dragOver: Ref<Maybe<string>>;
  onRowPointerDown: (source: T, event: PointerEvent) => void;
  cleanup: () => void;
} {
  const dragFrom = ref<Maybe<T>>(nothing);
  const dragOver = ref<Maybe<string>>(nothing);

  function endPointerDrag(clientX?: number, clientY?: number): void {
    const fromM = dragFrom.value;
    const hitM =
      typeof clientX !== "undefined" && typeof clientY !== "undefined"
        ? options.rowAtPoint(clientX, clientY)
        : dragOver.value;
    dragFrom.value = nothing;
    dragOver.value = nothing;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
    if (isNothing(fromM) || isNothing(hitM)) return;
    const from = fromM.value as T;
    const hit = hitM.value;
    if (from.key === hit) return;
    options.onDrop(from, hit);
  }

  function onPointerMove(event: PointerEvent): void {
    if ((event.buttons & 1) === 0) {
      cleanup();
      return;
    }
    if (isNothing(dragFrom.value)) return;
    event.preventDefault();
    const hitM = options.rowAtPoint(event.clientX, event.clientY);
    dragOver.value =
      isJust(hitM) && hitM.value !== dragFrom.value.value.key
        ? just(hitM.value)
        : nothing;
  }

  function onPointerUp(event: PointerEvent): void {
    endPointerDrag(event.clientX, event.clientY);
  }

  function onPointerCancel(): void {
    cleanup();
  }

  type DragCandidate = { source: T; startX: number; startY: number };
  let dragCandidate: Maybe<DragCandidate> = nothing;

  function clearDragCandidate(): void {
    dragCandidate = nothing;
    window.removeEventListener("pointermove", onCandidatePointerMove);
    window.removeEventListener("pointerup", onCandidatePointerUp);
    window.removeEventListener("pointercancel", onCandidatePointerCancel);
  }

  function onCandidatePointerMove(event: PointerEvent): void {
    if ((event.buttons & 1) === 0) {
      cleanup();
      return;
    }
    if (isNothing(dragCandidate)) return;
    const candidate = dragCandidate.value;
    const dx = event.clientX - candidate.startX;
    const dy = event.clientY - candidate.startY;
    if (Math.hypot(dx, dy) < POINTER_ROW_DRAG_THRESHOLD_PX) return;
    clearDragCandidate();
    event.preventDefault();
    dragFrom.value = just(candidate.source);
    dragOver.value = nothing;
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    onPointerMove(event);
  }

  function onCandidatePointerUp(): void {
    clearDragCandidate();
  }

  function onCandidatePointerCancel(): void {
    clearDragCandidate();
  }

  function onRowPointerDown(source: T, event: PointerEvent): void {
    cleanup();
    if (event.button !== 0 || event.ctrlKey) return;
    const target = event.target as HTMLElement;
    if (
      target.closest("input, textarea, [contenteditable]") !== null ||
      options.shouldIgnorePointerDown?.(target) === true
    ) {
      return;
    }
    dragCandidate = just({
      source,
      startX: event.clientX,
      startY: event.clientY,
    });
    window.addEventListener("pointermove", onCandidatePointerMove, {
      passive: false,
    });
    window.addEventListener("pointerup", onCandidatePointerUp);
    window.addEventListener("pointercancel", onCandidatePointerCancel);
  }

  function cleanup(): void {
    clearDragCandidate();
    dragFrom.value = nothing;
    dragOver.value = nothing;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
  }

  // Native menus/dialogs can consume pointerup before the WebView sees it.
  onMounted(() => {
    window.addEventListener("contextmenu", cleanup, true);
    window.addEventListener("blur", cleanup);
  });
  onUnmounted(() => {
    cleanup();
    window.removeEventListener("contextmenu", cleanup, true);
    window.removeEventListener("blur", cleanup);
  });

  return {
    dragFrom: dragFrom as Ref<Maybe<T>>,
    dragOver,
    onRowPointerDown,
    cleanup,
  };
}
